import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import { RefreshCw, Play, PlayCircle, AlertCircle, Database, UserCheck, FileText, Settings, HardDrive, BarChart2, Users, Shield, Search, Activity, Zap, Clock, Calendar, Cpu } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AdminPanel = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [isDebugLoading, setIsDebugLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('usage');
  const [systemStats, setSystemStats] = useState(null);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [isUserListLoading, setIsUserListLoading] = useState(false);

  // New state for usage data
  const [usageStats, setUsageStats] = useState(null);
  const [isUsageLoading, setIsUsageLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('7d'); // Default to last 7 days
  const [topEndpoints, setTopEndpoints] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  
  // Add back these state variables to fix build errors, but we won't show the lectures tab
  const [lectures, setLectures] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  
  // New state to control fallback data behavior
  const [useFallbackData, setUseFallbackData] = useState(false); // Set to false to use real data only
  
  // Add state for cost tracking
  const [totalCost, setTotalCost] = useState(0);
  const [userCosts, setUserCosts] = useState([]);
  
  // Define the base URL as a constant for easier maintenance
  const BASE_URL = 'https://classify-alpha-a1e5331e0be0.herokuapp.com';
  
  // Define token costs
  const COST_PER_INPUT_TOKEN = 0.10 / 1000000; // $0.10 per 1M tokens
  const COST_PER_OUTPUT_TOKEN = 0.40 / 1000000; // $0.40 per 1M tokens

  // Helper to get full API URL
  const getApiUrl = (endpoint) => `${BASE_URL}${endpoint}`;
  
  // Helper function to calculate costs based on tokens
  const calculateCost = (tokens, isInput = false) => {
    if (!tokens || isNaN(tokens)) return 0;
    
    // Apply the appropriate rate based on token type
    const rate = isInput ? COST_PER_INPUT_TOKEN : COST_PER_OUTPUT_TOKEN;
    return tokens * rate;
  };
  
  // Format cost as currency
  const formatCost = (cost) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    }).format(cost);
  };
  
  // Format large numbers with commas
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };
  
  // Process API usage data and calculate costs
  const processUsageData = (data) => {
    if (!data) return null;
    
    // Copy the data to avoid mutating the original
    const processedData = {...data};
    
    let totalCostValue = 0;
    let userCostMap = new Map();
    
    // Calculate costs for daily stats
    if (data.dailyStats && data.dailyStats.length > 0) {
      processedData.dailyStats = data.dailyStats.map(day => {
        // Estimate input/output token split (assuming 40% input, 60% output as an example)
        const inputTokens = Math.round(day.tokens * 0.4);
        const outputTokens = Math.round(day.tokens * 0.6);
        
        const inputCost = calculateCost(inputTokens, true);
        const outputCost = calculateCost(outputTokens, false);
        const totalDayCost = inputCost + outputCost;
        
        totalCostValue += totalDayCost;
        
        return {
          ...day,
          inputTokens,
          outputTokens,
          cost: totalDayCost
        };
      });
    }
    
    // Calculate costs for endpoints
    if (data.endpointStats) {
      processedData.endpointStats = data.endpointStats.map(endpoint => {
        // Estimate input/output token split
        const inputTokens = Math.round(endpoint.tokens * 0.4);
        const outputTokens = Math.round(endpoint.tokens * 0.6);
        
        const inputCost = calculateCost(inputTokens, true);
        const outputCost = calculateCost(outputTokens, false);
        const totalEndpointCost = inputCost + outputCost;
        
        return {
          ...endpoint,
          inputTokens,
          outputTokens,
          cost: totalEndpointCost
        };
      });
    }
    
    // Calculate costs for users
    if (data.topUsers) {
      processedData.topUsers = data.topUsers.map(user => {
        // Estimate input/output token split
        const inputTokens = Math.round(user.tokens * 0.4);
        const outputTokens = Math.round(user.tokens * 0.6);
        
        const inputCost = calculateCost(inputTokens, true);
        const outputCost = calculateCost(outputTokens, false);
        const totalUserCost = inputCost + outputCost;
        
        // Track costs by user for the sidebar
        userCostMap.set(user.user_id, {
          userId: user.user_id,
          calls: user.calls,
          tokens: user.tokens,
          cost: totalUserCost
        });
        
        return {
          ...user,
          inputTokens,
          outputTokens,
          cost: totalUserCost
        };
      });
      
      // Update user costs state
      setUserCosts(Array.from(userCostMap.values()));
    }
    
    // Update the total cost
    setTotalCost(totalCostValue);
    
    return processedData;
  };

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      // Check if the API endpoints are implemented yet
      checkApiEndpoints();
    }
  }, [isLoaded, isSignedIn]);

  // Add effect to refetch when timeRange changes
  useEffect(() => {
    if (isLoaded && isSignedIn && usageStats) {
      fetchUsageStats();
    }
  }, [timeRange]);

  // Add a function to check API endpoints availability and fetch data
  const checkApiEndpoints = async () => {
    console.log("Initializing API data fetch...");
    
    // Get the currently logged in user
    if (isLoaded && isSignedIn) {
      console.log("User authenticated, fetching data...");
    }
    
    // Directly fetch real data
    fetchUsageStats();
    fetchSubscriptions();
  };

  // Create fallback usage data for display purposes
  const setUsageFallbackData = () => {
    // Generate some fake data for demonstration
    const today = new Date();
    const dailyStats = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(today.getDate() - i);
      
      // Generate random tokens and calculate costs
      const tokens = Math.floor(Math.random() * 50000) + 10000;
      const inputTokens = Math.round(tokens * 0.4);
      const outputTokens = Math.round(tokens * 0.6);
      
      const inputCost = calculateCost(inputTokens, true);
      const outputCost = calculateCost(outputTokens, false);
      const totalCost = inputCost + outputCost;
      
      return {
        day: date.toLocaleDateString(),
        calls: Math.floor(Math.random() * 100) + 50,
        tokens: tokens,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        cost: totalCost
      };
    }).reverse();
    
    // Create fake endpoint data
    const endpoints = [
      'analyze-lecture', 'process-transcript', 'chat', 
      'generate-quiz', 'generate-flashcards', 'generate-concept-map',
      'generate-suggestions', 'generate-theme'
    ];
    
    const endpointStats = endpoints.map(endpoint => {
      const tokens = Math.floor(Math.random() * 100000) + 20000;
      const inputTokens = Math.round(tokens * 0.4);
      const outputTokens = Math.round(tokens * 0.6);
      
      const inputCost = calculateCost(inputTokens, true);
      const outputCost = calculateCost(outputTokens, false);
      const totalCost = inputCost + outputCost;
      
      return {
        endpoint: `/api/${endpoint}`,
        name: endpoint,
        calls: Math.floor(Math.random() * 300) + 50,
        tokens: tokens,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        cost: totalCost
      };
    });
    
    // Generate fake user data with costs
    const userCostsList = [];
    const topUsers = Array.from({ length: 5 }, (_, i) => {
      const userId = `user_${i+1}_${Math.random().toString(36).substring(2, 8)}`;
      const calls = Math.floor(Math.random() * 200) + 20;
      const tokens = Math.floor(Math.random() * 50000) + 5000;
      
      const inputTokens = Math.round(tokens * 0.4);
      const outputTokens = Math.round(tokens * 0.6);
      
      const inputCost = calculateCost(inputTokens, true);
      const outputCost = calculateCost(outputTokens, false);
      const totalCost = inputCost + outputCost;
      
      // Add to user costs list
      userCostsList.push({
        userId: userId,
        calls: calls,
        tokens: tokens,
        cost: totalCost
      });
      
      return {
        user_id: userId,
        calls: calls,
        tokens: tokens,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        cost: totalCost
      };
    });
    
    // Calculate total cost
    const totalCostValue = dailyStats.reduce((sum, day) => sum + day.cost, 0);
    
    // Update states
    setTotalCost(totalCostValue);
    setUserCosts(userCostsList);
    
    setUsageStats({
      overall: {
        total_calls: endpointStats.reduce((sum, ep) => sum + ep.calls, 0),
        total_tokens: endpointStats.reduce((sum, ep) => sum + ep.tokens, 0),
        unique_users: 15,
        total_cost: totalCostValue
      },
      formattedDailyStats: dailyStats,
      dailyStats: dailyStats,
      endpointStats: endpointStats,
      topUsers: topUsers
    });
    
    setTopEndpoints(endpointStats);
    setTopUsers(topUsers);
  };
  
  // Create fallback subscription data
  const setSubscriptionsFallbackData = () => {
    const plans = ['free', 'basic', 'premium', 'enterprise'];
    const statuses = ['active', 'trial', 'cancelled', 'expired'];
    
    const fakeSubs = Array.from({ length: 10 }, (_, i) => {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - Math.floor(Math.random() * 60));
      
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + (Math.floor(Math.random() * 90) + 30));
      
      return {
        id: i + 1,
        user_id: `user_${i+1}_${Math.random().toString(36).substring(2, 8)}`,
        plan_type: plans[Math.floor(Math.random() * plans.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        start_date: startDate.toISOString(),
        end_date: Math.random() > 0.3 ? endDate.toISOString() : null
      };
    });
    
    setSubscriptions(fakeSubs);
  };

  // New function to fetch usage statistics
  const fetchUsageStats = async () => {
    try {
      const url = getApiUrl('/api/admin/usage-stats');
      console.log("Fetching API usage statistics from:", url);
      setIsUsageLoading(true);
      
      // Add request debugging
      console.log("Request headers:", {
        auth: "Authenticated request via Clerk",
        timestamp: new Date().toISOString()
      });
      
      const response = await authenticatedFetch(url);
      
      console.log("Response status:", response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log("API Usage data received:", data);
        
        // Check if this is real data or empty data
        const hasRealData = data && 
                          data.overall && 
                          (data.dailyStats && data.dailyStats.length > 0);
        
        if (hasRealData) {
          console.log("✅ Using real API usage data");
          
          // Process data and calculate costs
          const processedData = processUsageData(data);
          
          // Set usage stats with the processed data
          setUsageStats(processedData);
          
          // Continue with additional data processing for charts
          if (processedData.dailyStats) {
            // Format dates and limit to the selected time range
            const daysToShow = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
            const formattedDailyStats = processedData.dailyStats
              .slice(0, daysToShow)
              .map(stat => ({
                ...stat,
                day: new Date(stat.day).toLocaleDateString(),
                calls: Number(stat.calls),
                tokens: Number(stat.tokens) || 0,
                cost: stat.cost || 0
              }))
              .reverse(); // Show oldest to newest
              
            setUsageStats(prev => ({ ...prev, formattedDailyStats }));
          }
          
          // Process top endpoints
          if (processedData.endpointStats) {
            const formatted = processedData.endpointStats
              .map(stat => ({
                ...stat,
                calls: Number(stat.calls),
                tokens: Number(stat.tokens) || 0,
                cost: stat.cost || 0,
                name: stat.endpoint.replace('/api/', '')
              }))
              .slice(0, 10); // Top 10 endpoints
              
            setTopEndpoints(formatted);
          }
          
          // Top users is already processed in processUsageData function
          if (processedData.topUsers) {
            setTopUsers(processedData.topUsers);
          }
          
        } else {
          console.warn("⚠️ API returned empty or incomplete data");
          toast.error("No API usage data available yet. Please make some API calls to generate usage statistics.");
          setUsageStats({
            overall: { total_calls: 0, total_tokens: 0, unique_users: 0, total_cost: 0 },
            formattedDailyStats: [],
            dailyStats: [],
            endpointStats: [],
            topUsers: []
          });
          setTopEndpoints([]);
          setTopUsers([]);
          // No fallback data, show empty state
        }
      } else {
        // Handle non-OK responses with better error handling
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        try {
          // Try to extract error message from response
          const errorData = await response.text();
          console.error('Error response:', errorData);
          // Check if it's JSON or HTML
          if (errorData.trim().startsWith('{')) {
            const errorJson = JSON.parse(errorData);
            if (errorJson.error) {
              errorMessage = errorJson.error;
            }
          } else if (errorData.includes('<!doctype') || errorData.includes('<html')) {
            errorMessage = `Server returned HTML instead of JSON (${response.status})`;
            console.error('API endpoint not found or returned HTML');
          }
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        toast.error(`Failed to fetch usage statistics: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      toast.error(`API usage statistics error: ${error.message}`);
    } finally {
      setIsUsageLoading(false);
    }
  };

  // Fetch subscriptions
  const fetchSubscriptions = async () => {
    try {
      const url = getApiUrl('/api/admin/subscriptions');
      console.log("Fetching subscriptions from:", url);
      setIsSubscriptionsLoading(true);
      const response = await authenticatedFetch(url);
      
      if (response.ok) {
        const data = await response.json();
        console.log("Subscription data received:", data);
        
        // Check if we got real data
        const hasRealData = data && Array.isArray(data) && data.length > 0;
        
        if (hasRealData) {
          console.log("✅ Using real subscription data");
          setSubscriptions(data);
        } else {
          console.warn("⚠️ API returned empty subscription data");
          toast.error("No subscription data available. Please add subscriptions to the database.");
          setSubscriptions([]);
        }
      } else {
        // Handle non-OK responses with better error handling
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        try {
          // Try to extract error message from response
          const errorData = await response.text();
          console.error('Error response:', errorData);
          // Check if it's JSON or HTML
          if (errorData.trim().startsWith('{')) {
            const errorJson = JSON.parse(errorData);
            if (errorJson.error) {
              errorMessage = errorJson.error;
            }
          } else if (errorData.includes('<!doctype') || errorData.includes('<html')) {
            errorMessage = `Server returned HTML instead of JSON (${response.status})`;
            console.error('API endpoint not found or returned HTML');
          }
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        
        toast.error(`Failed to fetch subscription data: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error(`Subscription data error: ${error.message}`);
    } finally {
      setIsSubscriptionsLoading(false);
    }
  };

  // Add system stats function
  const fetchSystemStats = async () => {
    setIsStatsLoading(true);
    try {
      const response = await authenticatedFetch(getApiUrl('/api/admin/system-stats'));
      
      if (response.ok) {
        const data = await response.json();
        setSystemStats(data);
      } else {
        toast.error('Failed to fetch system statistics');
      }
    } catch (error) {
      console.error('Error fetching system stats:', error);
      toast.error('Failed to fetch system statistics');
    } finally {
      setIsStatsLoading(false);
    }
  };

  // Add user list function
  const fetchUserList = async () => {
    setIsUserListLoading(true);
    try {
      const response = await authenticatedFetch(getApiUrl('/api/admin/users'));
      
      if (response.ok) {
        const data = await response.json();
        setUserList(data);
      } else {
        toast.error('Failed to fetch user list');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch user list');
    } finally {
      setIsUserListLoading(false);
    }
  };

  // Add debug data function
  const fetchDebugData = async () => {
    setIsDebugLoading(true);
    try {
      const response = await authenticatedFetch(getApiUrl('/api/debug/lectures'));
      
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
      } else {
        toast.error('Failed to fetch debug data');
      }
    } catch (error) {
      console.error('Error fetching debug data:', error);
      toast.error('Failed to fetch debug data');
    } finally {
      setIsDebugLoading(false);
    }
  };

  const fetchLectures = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedFetch(getApiUrl('/api/admin/lectures/needs-analysis'));
      
      if (response.ok) {
        const data = await response.json();
        setLectures(data);
      } else {
        toast.error('Failed to fetch lectures');
      }
    } catch (error) {
      console.error('Error fetching lectures:', error);
      toast.error('An error occurred while fetching lectures');
    } finally {
      setIsLoading(false);
    }
  };

  const reanalyzeLecture = async (lectureId) => {
    try {
      const response = await authenticatedFetch(getApiUrl(`/api/lectures/${lectureId}/reanalyze`), {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Lecture reanalyzed successfully');
        fetchLectures();
      } else {
        toast.error('Failed to reanalyze lecture');
      }
    } catch (error) {
      console.error('Error reanalyzing lecture:', error);
      toast.error('An error occurred while reanalyzing the lecture');
    }
  };

  const bulkReanalyze = async () => {
    try {
      setIsBulkProcessing(true);
      setProgress({ current: 0, total: lectures.length });
      
      let processed = 0;
      let successful = 0;
      
      for (const lecture of lectures) {
        try {
          const response = await authenticatedFetch(getApiUrl(`/api/lectures/${lecture.id}/reanalyze`), {
            method: 'POST'
          });
          
          if (response.ok) {
            successful++;
          }
        } catch (error) {
          console.error(`Error reanalyzing lecture ${lecture.id}:`, error);
        }
        
        processed++;
        setProgress({ current: processed, total: lectures.length });
      }
      
      toast.success(`Completed reanalysis. Successfully processed ${successful} of ${lectures.length} lectures.`);
      fetchLectures();
    } catch (error) {
      console.error('Error in bulk reanalyze:', error);
      toast.error('An error occurred during bulk reanalysis');
    } finally {
      setIsBulkProcessing(false);
      setProgress(null);
    }
  };

  const convertDebugDataToLectures = () => {
    if (!debugData || !debugData.recentLectures) {
      return;
    }
    
    const needsAnalysisLectures = debugData.recentLectures.filter(
      lecture => lecture.analysis_type !== 'HAS_CONTENT'
    );
    
    setLectures(needsAnalysisLectures);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'usage':
        return renderUsageTab();
      case 'system':
        return renderSystemTab();
      case 'users':
        return renderUsersTab();
      // We'll keep the lectures tab case but we won't show it in the navigation
      case 'lectures':
        return renderLecturesTab();
      default:
        return renderUsageTab();
    }
  };

  // Keep this function for backward compatibility
  const renderLecturesTab = () => (
    <div>
      <div className="text-center py-10 text-gray-500">
        <p>Lecture analysis has been moved to the API Usage dashboard.</p>
      </div>
    </div>
  );

  // Now let's update the renderUsageTab function to display cost information
  const renderUsageTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 
          className="text-2xl font-bold"
          style={{ color: theme.text }}
        >
          API Usage Dashboard
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-3 py-1 rounded text-sm`}
            style={{ 
              backgroundColor: timeRange === '7d' ? theme.text : theme.secondary,
              color: timeRange === '7d' ? theme.background : theme.text 
            }}
          >
            7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-3 py-1 rounded text-sm`}
            style={{ 
              backgroundColor: timeRange === '30d' ? theme.text : theme.secondary,
              color: timeRange === '30d' ? theme.background : theme.text
            }}
          >
            30 Days
          </button>
          <button
            onClick={() => setTimeRange('90d')}
            className={`px-3 py-1 rounded text-sm`}
            style={{ 
              backgroundColor: timeRange === '90d' ? theme.text : theme.secondary,
              color: timeRange === '90d' ? theme.background : theme.text
            }}
          >
            90 Days
          </button>
          <button
            onClick={fetchUsageStats}
            className="ml-4 p-2 rounded"
            style={{ 
              backgroundColor: theme.secondary,
              color: theme.text
            }}
            disabled={isUsageLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isUsageLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isUsageLoading ? (
        <div className="flex justify-center items-center h-64">
          <div 
            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
            style={{ borderColor: theme.primary }}
          ></div>
        </div>
      ) : usageStats ? (
        <>
          {/* Usage Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard 
              title="Total API Calls" 
              value={usageStats.overall?.total_calls || '0'} 
              icon={<Activity />} 
              theme="blue" 
            />
            <StatCard 
              title="Total Tokens" 
              value={usageStats.overall?.total_tokens ? formatNumber(Math.round(usageStats.overall.total_tokens)) : '0'} 
              icon={<Zap />} 
              theme="yellow" 
            />
            <StatCard 
              title="Unique Users" 
              value={usageStats.overall?.unique_users || '0'} 
              icon={<Users />} 
              theme="green" 
            />
            <StatCard 
              title="Total Cost" 
              value={usageStats.overall?.total_cost ? formatCost(usageStats.overall.total_cost) : formatCost(totalCost)} 
              icon={<Cpu />} 
              theme="purple" 
            />
            <StatCard 
              title="Active Subscriptions" 
              value={subscriptions.filter(sub => sub.status === 'active').length} 
              icon={<Shield />} 
              theme="indigo" 
            />
          </div>
          
          {/* API Usage Over Time Chart */}
          <div 
            className="mt-6 p-4 rounded-lg shadow"
            style={{ 
              backgroundColor: theme.background,
              border: `1px solid ${theme.border}`
            }}
          >
            <h3 
              className="text-lg font-semibold mb-4"
              style={{ color: theme.text }}
            >
              API Usage Over Time
            </h3>
            {usageStats.formattedDailyStats && usageStats.formattedDailyStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={usageStats.formattedDailyStats}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                  <XAxis dataKey="day" stroke={theme.text} />
                  <YAxis yAxisId="left" stroke={theme.primary} />
                  <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.background, 
                      borderColor: theme.border,
                      color: theme.text
                    }}
                    formatter={(value, name) => {
                      if (name === 'Cost') return formatCost(value);
                      return formatNumber(value);
                    }}
                  />
                  <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="calls" stroke={theme.text} name="API Calls" />
                  <Line yAxisId="right" type="monotone" dataKey="tokens" stroke="#F59E0B" name="Tokens Used" />
                  <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#10B981" name="Cost" dot={true} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div 
                className="text-center py-10"
                style={{ color: theme.accent }}
              >
                No usage data available for this time period
                </div>
              )}
            </div>

          {/* Top Endpoints and Top Users */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Top Endpoints */}
            <div 
              className="rounded-lg shadow p-4"
              style={{ 
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`
              }}
            >
              <h3 
                className="text-lg font-semibold mb-4"
                style={{ color: theme.text }}
              >
                Top Endpoints by Cost
              </h3>
              {topEndpoints.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topEndpoints.sort((a, b) => b.cost - a.cost).slice(0, 5)}
                    margin={{ top: 5, right: 30, left: 20, bottom: 70 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                    <XAxis type="number" stroke={theme.text} />
                    <YAxis type="category" dataKey="name" width={150} stroke={theme.text} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: theme.background, 
                        borderColor: theme.border,
                        color: theme.text
                      }}
                      formatter={(value, name) => {
                        if (name === 'Cost') return formatCost(value);
                        return formatNumber(value);
                      }}
                    />
                    <Legend />
                    <Bar dataKey="calls" fill={theme.text} name="API Calls" />
                    <Bar dataKey="cost" fill="#10B981" name="Cost" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div 
                  className="text-center py-10"
                  style={{ color: theme.accent }}
                >
                  No endpoint data available
            </div>
              )}
          </div>
          
            {/* Top Users */}
            <div 
              className="rounded-lg shadow p-4"
              style={{ 
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`
              }}
            >
              <h3 
                className="text-lg font-semibold mb-4"
                style={{ color: theme.text }}
              >
                Top Users by API Usage
              </h3>
            <div className="overflow-x-auto">
                <table 
                  className="min-w-full divide-y"
                  style={{ 
                    borderColor: theme.border,
                    color: theme.text
                  }}
                >
                <thead>
                    <tr>
                      <th 
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: theme.accent }}
                      >
                        User ID
                      </th>
                      <th 
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: theme.accent }}
                      >
                        API Calls
                      </th>
                      <th 
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: theme.accent }}
                      >
                        Tokens Used
                      </th>
                      <th 
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: theme.accent }}
                      >
                        Cost
                      </th>
                  </tr>
                </thead>
                  <tbody 
                    className="divide-y"
                    style={{ borderColor: theme.border }}
                  >
                    {topUsers.length > 0 ? (
                      topUsers.map((user, index) => (
                        <tr key={index}>
                          <td 
                            className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                            style={{ color: theme.text }}
                          >
                            {user.user_id.substring(0, 8)}...
                      </td>
                          <td 
                            className="px-4 py-2 whitespace-nowrap text-sm"
                            style={{ color: theme.text }}
                          >
                            {formatNumber(user.calls)}
                      </td>
                          <td 
                            className="px-4 py-2 whitespace-nowrap text-sm"
                            style={{ color: theme.text }}
                          >
                            {user.tokens ? formatNumber(user.tokens) : 'N/A'}
                          </td>
                          <td 
                            className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                            style={{ color: theme.success }}
                          >
                            {formatCost(user.cost)}
                      </td>
                    </tr>
                      ))
                    ) : (
                      <tr>
                        <td 
                          colSpan="4" 
                          className="px-4 py-2 text-center text-sm"
                          style={{ color: theme.accent }}
                        >
                          No user data available
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
          
          {/* Subscriptions */}
          <div 
            className="mt-6 p-4 rounded-lg shadow"
              style={{ 
              backgroundColor: theme.background,
              border: `1px solid ${theme.border}`
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 
                className="text-lg font-semibold"
                style={{ color: theme.text }}
              >
                Active Subscriptions
              </h3>
            <button 
                onClick={fetchSubscriptions}
                className="p-2 rounded"
                style={{ 
                  backgroundColor: theme.secondary,
                  color: theme.text
                }}
                disabled={isSubscriptionsLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isSubscriptionsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
            <div className="overflow-x-auto">
              <table 
                className="min-w-full divide-y"
                style={{ 
                  borderColor: theme.border,
                  color: theme.text
                }}
              >
                <thead>
                  <tr>
                    <th 
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      User ID
                    </th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      Plan
                    </th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      Status
                    </th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      Start Date
                    </th>
                    <th 
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: theme.accent }}
                    >
                      End Date
                    </th>
              </tr>
            </thead>
                <tbody 
                  className="divide-y"
                  style={{ borderColor: theme.border }}
                >
                  {subscriptions.length > 0 ? (
                    subscriptions.map((sub, index) => (
                      <tr key={index}>
                        <td 
                          className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                          style={{ color: theme.text }}
                        >
                          {sub.user_id.substring(0, 8)}...
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <span 
                            className="px-2 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                              backgroundColor: 
                                sub.plan_type === 'premium' ? '#8B5CF6' :
                                sub.plan_type === 'basic' ? '#3B82F6' :
                                sub.plan_type === 'enterprise' ? '#4F46E5' : '#D1D5DB',
                        color: theme.background
                      }}
                    >
                            {sub.plan_type.charAt(0).toUpperCase() + sub.plan_type.slice(1)}
                    </span>
                  </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-semibold"
                      style={{ 
                              backgroundColor: 
                                sub.status === 'active' ? '#10B981' :
                                sub.status === 'trial' ? '#F59E0B' : '#EF4444',
                        color: theme.background
                      }}
                    >
                            {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                          </span>
                        </td>
                        <td 
                          className="px-4 py-2 whitespace-nowrap text-sm"
                          style={{ color: theme.text }}
                        >
                          {new Date(sub.start_date).toLocaleDateString()}
                        </td>
                        <td 
                          className="px-4 py-2 whitespace-nowrap text-sm"
                          style={{ color: theme.text }}
                        >
                          {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
                    ))
                  ) : (
                    <tr>
                      <td 
                        colSpan="5" 
                        className="px-4 py-2 text-center text-sm"
                        style={{ color: theme.accent }}
                      >
                        No subscription data available
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
        </div>
        </>
      ) : (
        <div 
          className="text-center py-10"
          style={{ backgroundColor: theme.background, color: theme.accent }}
        >
          <p>No usage data available. Click refresh to load data.</p>
          <button
            onClick={fetchUsageStats}
            className="mt-4 px-4 py-2 rounded hover:opacity-90 transition"
            style={{ 
              backgroundColor: theme.primary,
              color: theme.background
            }}
          >
            Refresh Data
          </button>
        </div>
      )}
    </div>
  );

  const renderSystemTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
          System Statistics
        </h2>
        <button
          onClick={fetchSystemStats}
          disabled={isStatsLoading}
          className="flex items-center gap-2 px-4 py-2 rounded text-white"
          style={{ 
            backgroundColor: isStatsLoading ? theme.accent : theme.primary,
            color: theme.background,
            border: `1px solid ${theme.border}`
          }}
        >
          <BarChart2 size={16} className={isStatsLoading ? "animate-spin" : ""} />
          {isStatsLoading ? 'Loading...' : 'Refresh Stats'}
        </button>
      </div>

      {isStatsLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-pulse" style={{ color: theme.accent }}>Loading system statistics...</div>
        </div>
      ) : systemStats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard 
            title="Total Users" 
            value={systemStats.totalUsers || 0} 
            icon={<Users size={24} />} 
            theme={theme} 
          />
          <StatCard 
            title="Total Classes" 
            value={systemStats.totalClasses || 0} 
            icon={<FileText size={24} />} 
            theme={theme} 
          />
          <StatCard 
            title="Total Lectures" 
            value={systemStats.totalLectures || 0} 
            icon={<FileText size={24} />} 
            theme={theme} 
          />
          <StatCard 
            title="Processed Lectures" 
            value={systemStats.processedLectures || 0} 
            icon={<HardDrive size={24} />} 
            theme={theme} 
          />
          <StatCard 
            title="Storage Used" 
            value={systemStats.storageUsed || '0 MB'} 
            icon={<Database size={24} />} 
            theme={theme} 
          />
          <StatCard 
            title="API Requests (24h)" 
            value={systemStats.apiRequests24h || 0} 
            icon={<Settings size={24} />} 
            theme={theme} 
          />
        </div>
      ) : (
        <div className="p-6 text-center rounded" style={{ backgroundColor: theme.secondary, borderColor: theme.border }}>
          <AlertCircle className="mx-auto h-12 w-12 mb-4" style={{ color: theme.accent }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.text }}>No System Stats Available</h3>
          <p style={{ color: theme.accent }}>
            Click the refresh button to fetch system statistics.
          </p>
        </div>
      )}
    </div>
  );

  const renderUsersTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
          User Management
        </h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5" style={{ color: theme.accent }} />
            <input 
              type="text" 
              placeholder="Search users..." 
              className="pl-10 pr-4 py-2 rounded"
              style={{ 
                backgroundColor: theme.background, 
                color: theme.text,
                border: `1px solid ${theme.border}`
              }}
            />
          </div>
          <button
            onClick={fetchUserList}
            disabled={isUserListLoading}
            className="flex items-center gap-2 px-4 py-2 rounded text-white"
            style={{ 
              backgroundColor: isUserListLoading ? theme.accent : theme.primary,
              color: theme.background,
              border: `1px solid ${theme.border}`
            }}
          >
            <RefreshCw size={16} className={isUserListLoading ? "animate-spin" : ""} />
            {isUserListLoading ? 'Loading...' : 'Refresh Users'}
          </button>
        </div>
      </div>

      {isUserListLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-pulse" style={{ color: theme.accent }}>Loading users...</div>
        </div>
      ) : userList.length > 0 ? (
        <div className="overflow-x-auto rounded shadow" style={{ backgroundColor: theme.background, border: `1px solid ${theme.border}` }}>
          <table className="min-w-full">
            <thead style={{ backgroundColor: theme.secondary }}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: theme.text }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: theme.border }}>
              {userList.map((user) => (
                <tr key={user.id} style={{ backgroundColor: theme.background }}>
                  <td className="px-4 py-3 text-sm" style={{ color: theme.text }}>{user.id}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: theme.text }}>{user.name || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm" style={{ color: theme.text }}>{user.email}</td>
                  <td className="px-4 py-3 text-sm">
                    <span 
                      className="px-2 py-1 rounded text-xs"
                      style={{ 
                        backgroundColor: user.isAdmin ? theme.primary : theme.accent,
                        color: theme.background
                      }}
                    >
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span 
                      className="px-2 py-1 rounded text-xs"
                      style={{ 
                        backgroundColor: user.isActive ? theme.success : theme.error,
                        color: theme.background
                      }}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      className="px-3 py-1 rounded text-sm flex items-center gap-1 mr-2"
                      style={{ 
                        backgroundColor: theme.primary,
                        color: theme.background
                      }}
                    >
                      <Shield size={12} />
                      Toggle Admin
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-6 text-center rounded" style={{ backgroundColor: theme.secondary, borderColor: theme.border }}>
          <AlertCircle className="mx-auto h-12 w-12 mb-4" style={{ color: theme.accent }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.text }}>No Users Found</h3>
          <p style={{ color: theme.accent }}>
            Click the refresh button to fetch user data.
          </p>
        </div>
      )}
    </div>
  );

  const StatCard = ({ title, value, icon, theme: cardTheme }) => (
    <div 
      className="p-4 rounded-lg shadow"
      style={{ 
        backgroundColor: theme.background,
        border: `1px solid ${theme.border}`,
        color: theme.text
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium" style={{ color: theme.accent }}>{title}</div>
        <div 
          className="p-2 rounded-full"
          style={{ 
            backgroundColor: cardTheme === 'blue' ? '#3B82F6' :
                            cardTheme === 'green' ? '#10B981' :
                            cardTheme === 'yellow' ? '#F59E0B' :
                            cardTheme === 'purple' ? '#8B5CF6' :
                            cardTheme === 'red' ? '#EF4444' : theme.primary,
            color: theme.background
          }}
        >
          {icon}
        </div>
      </div>
      <div 
        className="text-2xl font-bold"
        style={{ color: theme.text }}
      >
        {value}
      </div>
    </div>
  );

  if (!isLoaded || !isSignedIn) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  return (
    <div 
      className="container mx-auto p-4"
      style={{ color: theme.text }}
    >
      <h1 
        className="text-3xl font-bold mb-6"
        style={{ color: theme.text }}
      >
        Admin Panel
      </h1>
      
      {/* Tabs */}
      <div 
        className="flex border-b mb-6 overflow-x-auto"
        style={{ borderColor: theme.border }}
      >
        <button
          className={`px-4 py-2 font-medium`}
          style={{ 
            borderBottom: activeTab === 'usage' ? `2px solid ${theme.primary}` : 'none',
            color: activeTab === 'usage' ? theme.text : theme.accent
          }}
          onClick={() => setActiveTab('usage')}
        >
          <div className="flex items-center space-x-2">
            <Activity className="h-4 w-4" />
            <span>API Usage</span>
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium`}
          style={{ 
            borderBottom: activeTab === 'system' ? `2px solid ${theme.primary}` : 'none',
            color: activeTab === 'system' ? theme.text : theme.accent
          }}
          onClick={() => {
            setActiveTab('system');
            if (!systemStats) fetchSystemStats();
          }}
        >
          <div className="flex items-center space-x-2">
            <HardDrive className="h-4 w-4" />
            <span>System</span>
          </div>
        </button>
        <button
          className={`px-4 py-2 font-medium`}
          style={{ 
            borderBottom: activeTab === 'users' ? `2px solid ${theme.primary}` : 'none',
            color: activeTab === 'users' ? theme.text : theme.accent
          }}
          onClick={() => {
            setActiveTab('users');
            if (userList.length === 0) fetchUserList();
          }}
        >
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4" />
            <span>Users</span>
          </div>
        </button>
      </div>
      
      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default AdminPanel; 